<template>
  <div
    class="grid"
    :class="{ 'grid--window': windowed }"
    role="grid"
    aria-label="Crossword grid"
    @pointerleave="peekOff('mouse')"
    @pointermove="drag"
    @pointerup="up"
    @pointercancel="cancel"
    @wheel="wheel"
    @contextmenu.prevent
  >
    <div ref="cells" class="grid__cells" :style="{ '--n': tracks, '--span': span }">
      <template v-for="r in rowsShown" :key="r">
        <template v-for="c in colsShown" :key="`${r}-${c}`">
          <div
            v-if="r < puzzle.size && c < puzzle.size"
            class="cell"
            :class="cellClass(r, c, puzzle.grid[r][c])"
            :style="cellStyle(r, c)"
            role="gridcell"
            @pointerdown.prevent="down($event, r, c, puzzle.grid[r][c])"
            @pointerenter="hover($event, r, c)"
          >
            <span v-if="numberAt(r, c)" class="cell__num">{{ numberAt(r, c) }}</span>
            <span class="cell__letter">{{ letterAt(r, c) }}</span>
            <span v-if="checked[k(r, c)]" class="cell__wrong" aria-hidden="true" />
          </div>
          <div v-else class="cell cell--black" aria-hidden="true" />
        </template>
      </template>
      <div v-if="peekTag" class="grid__peek" :style="peekTag.style" aria-hidden="true">
        <span v-for="o in peekTag.people" :key="o.uid" :style="{ background: o.color }">{{ o.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// The grid: 15x15, or 21x21 on Sundays (--n is how many squares are shown
// across). A Giant (101x101) is shown through a window of `view` squares
// that follows the cursor - keeping the whole current word in sight when it
// fits - and can be dragged about, or scrolled with a mouse wheel (Shift
// for sideways) or a trackpad (Matt, 2026-10-29); GiantMap shows the whole
// thing. Scrolling and dragging glide - the window sits part-way between
// squares, an extra row and column drawn and the lot slid by the fraction -
// and when they stop it eases onto whole squares (Matt, 2026-10-29: a square
// at a time "makes the scrolling feel really janky"). Only `origin` (whole
// squares) is reactive; the fraction is a CSS variable set by hand, so a
// glide re-renders the squares only when a new row or column comes in.
// A CSS grid of real cells (not an SVG) so every square is its
// own tap target and the active word / cursor / other people's cursors are
// plain classes. Sized by the parent against the viewport height - the
// house lesson from every board game here: a square grid that follows the
// width walks off the bottom of a phone once the keyboard is up.
// Who's that ring? Hover a square (mouse) or press and hold it (touch) and
// a name tag for everyone else on it floats above - the grid's own child,
// so the cell's overflow never clips it.
import { mapState, mapGetters } from 'vuex';
import { numberGrid } from '@/engine/geometry';
import { key, letterAt as letterIn } from '@/engine/solving';

const WHEEL_REST_MS = 100;
const SETTLE_MS = 150;

export default {
  name: 'PuzzleGrid',
  props: {
    revealTheme: { type: Boolean, default: false },
    revealStep: { type: Number, default: 0 },
    // Squares shown across a Giant's window; 0 shows the whole grid.
    view: { type: Number, default: 0 },
  },
  emits: ['tap', 'window'],
  data() {
    return { peek: null, origin: { r: 0, c: 0 }, press: null };
  },
  computed: {
    ...mapState(['puzzle', 'letters', 'cursor', 'checked', 'reveals', 'solvedAt']),
    ...mapGetters(['currentCells', 'others', 'myUid']),
    numbers() { return numberGrid(this.puzzle.grid).numbers; },
    windowed() { return this.view > 0 && this.view < this.puzzle.size; },
    span() { return this.windowed ? this.view : this.puzzle.size; },
    // A window draws one more row and column than it shows, for the glide.
    tracks() { return this.windowed ? this.span + 1 : this.span; },
    rowsShown() { return Array.from({ length: this.tracks }, (_, i) => this.origin.r + i); },
    colsShown() { return Array.from({ length: this.tracks }, (_, i) => this.origin.c + i); },
    // Once solved, nothing that follows a cursor is drawn - no word shading,
    // no cursor square, no rings (Matt, 2026-10-22: a ring left on a theme
    // entry looked like half the reveal).
    wordSet() { return this.solvedAt ? new Set() : new Set(this.currentCells.map(([r, c]) => key(r, c))); },
    // Theme entries by cell, with the order they light up on the reveal.
    themeCellOrder() {
      const map = {};
      const entries = [...this.puzzle.across, ...this.puzzle.down].filter((e) => e.theme);
      entries.forEach((e, i) => {
        for (let j = 0; j < e.len; j += 1) {
          const r = e.row + (e.dir === 'down' ? j : 0);
          const c = e.col + (e.dir === 'down' ? 0 : j);
          map[key(r, c)] = i + 1;
        }
      });
      return map;
    },
    otherCursors() {
      const map = {};
      if (this.solvedAt) return map;
      for (const o of this.others) {
        if (o.row == null) continue;
        const kk = key(o.row, o.col);
        (map[kk] ||= []).push({ uid: o.uid, name: o.name || 'Someone', color: o.color || '#fff' });
      }
      return map;
    },
    // The tag sits above the square (below it on the top row), pinned to
    // the grid's edge in the outer columns so it never hangs off.
    peekTag() {
      if (!this.peek) return null;
      const people = this.otherCursors[key(this.peek.row, this.peek.col)];
      if (!people) return null;
      const row = this.peek.row - this.origin.r;
      const col = this.peek.col - this.origin.c;
      const n = this.span;
      const t = this.tracks;
      const top = row === 0;
      let x = '-50%';
      if (col === 0) x = '0';
      else if (col === n - 1) x = '-100%';
      return {
        people,
        style: {
          left: `${((col + 0.5) / t) * 100}%`,
          top: `${((top ? row + 1 : row) / t) * 100}%`,
          transform: `translate(${x}, ${top ? '4px' : 'calc(-100% - 4px)'})`,
        },
      };
    },
  },
  watch: {
    cursor: { handler() { this.follow(); }, deep: true },
    currentCells() { this.follow(); },
    view() { this.follow(); },
    origin: { handler(o) { this.$emit('window', { ...o, span: this.span }); }, deep: true },
  },
  created() { this.pos = { r: 0, c: 0 }; },
  mounted() { this.follow(); this.$emit('window', { ...this.origin, span: this.span }); },
  // A new origin's squares are in place: now slide them by the fraction.
  updated() { this.slide(); },
  methods: {
    k: key,
    // Keep the cursor's word in the window (or, for a word too long to fit,
    // the cursor and a square either side), moving as little as possible.
    follow() {
      if (!this.windowed) { this.stopGlide(); this.moveTo(0, 0); return; }
      const n = this.puzzle.size;
      const s = this.span;
      const cells = this.currentCells.length ? this.currentCells : [[this.cursor.row, this.cursor.col]];
      const fit = (lo, hi, at, start) => {
        let from = start;
        const [a, b] = hi - lo + 3 <= s ? [lo - 1, hi + 1] : [at - 1, at + 1];
        if (a < from) from = a;
        if (b >= from + s) from = b - s + 1;
        return Math.max(0, Math.min(n - s, from));
      };
      const rows = cells.map(([r]) => r);
      const cols = cells.map(([, c]) => c);
      const r = fit(Math.min(...rows), Math.max(...rows), this.cursor.row, this.origin.r);
      const c = fit(Math.min(...cols), Math.max(...cols), this.cursor.col, this.origin.c);
      if (r !== this.origin.r || c !== this.origin.c) { this.stopGlide(); this.moveTo(r, c); }
    },
    // The window's top-left corner, in squares, fractions allowed.
    moveTo(r, c) {
      const max = Math.max(0, this.puzzle.size - this.span);
      const clamp = (v) => Math.max(0, Math.min(max, v));
      this.pos = { r: clamp(r), c: clamp(c) };
      const o = { r: Math.floor(this.pos.r), c: Math.floor(this.pos.c) };
      if (o.r !== this.origin.r || o.c !== this.origin.c) this.origin = o; // slide() follows the render
      else this.slide();
    },
    slide() {
      const el = this.$refs.cells;
      if (!el) return;
      el.style.setProperty('--ox', String(this.pos.c - this.origin.c));
      el.style.setProperty('--oy', String(this.pos.r - this.origin.r));
    },
    // Once the scrolling stops, ease onto the nearest whole square.
    settleSoon(ms) {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), ms);
    },
    settle() {
      this.stopGlide();
      const from = { ...this.pos };
      const to = { r: Math.round(from.r), c: Math.round(from.c) };
      if (to.r === from.r && to.c === from.c) return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { this.moveTo(to.r, to.c); return; }
      const start = Date.now();
      const step = () => {
        const t = Math.min(1, (Date.now() - start) / SETTLE_MS);
        const e = 1 - (1 - t) ** 3;
        this.moveTo(from.r + (to.r - from.r) * e, from.c + (to.c - from.c) * e);
        this.glideFrame = t < 1 ? requestAnimationFrame(step) : null;
      };
      this.glideFrame = requestAnimationFrame(step);
    },
    stopGlide() {
      clearTimeout(this.settleTimer);
      if (this.glideFrame) cancelAnimationFrame(this.glideFrame);
      this.glideFrame = null;
    },
    numberAt(r, c) { return this.numbers[`${r},${c}`] || null; },
    letterAt(r, c) { return letterIn(this.letters, r, c); },
    byOther(r, c) {
      const v = this.letters[key(r, c)];
      return v && typeof v === 'object' && v.by && v.by !== this.myUid;
    },
    cellClass(r, c, ch) {
      const kk = key(r, c);
      const theme = this.themeCellOrder[kk];
      return {
        'cell--black': ch === '#',
        'cell--word': this.wordSet.has(kk),
        'cell--cursor': !this.solvedAt && this.cursor.row === r && this.cursor.col === c,
        'cell--revealed': Boolean(this.reveals[kk]),
        'cell--other': this.byOther(r, c),
        'cell--theme': this.revealTheme && theme && theme <= this.revealStep,
        'cell--solved': Boolean(this.solvedAt) && ch !== '#',
      };
    },
    cellStyle(r, c) {
      const kk = key(r, c);
      const style = {};
      const other = this.otherCursors[kk];
      if (other) style['--other-color'] = other[0].color;
      const v = this.letters[kk];
      if (this.byOther(r, c) && v?.by) {
        const player = this.$store.state.roomMeta?.players?.[v.by];
        if (player?.color) style['--ink-other'] = player.color;
      }
      return style;
    },
    down(e, r, c, ch) {
      // In a window a press might be the start of a drag, so the tap waits
      // for the finger to lift without having moved.
      if (this.windowed) {
        this.stopGlide();
        this.press = { x: e.clientX, y: e.clientY, r, c, ch, from: { ...this.pos }, moved: false };
        this.$el.setPointerCapture?.(e.pointerId);
      } else {
        this.tap(r, c, ch);
      }
      if (e.pointerType === 'mouse') return;
      clearTimeout(this.holdTimer);
      this.peek = null;
      this.holdTimer = setTimeout(() => { this.peek = { row: r, col: c, by: 'touch' }; }, 500);
    },
    drag(e) {
      const p = this.press;
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!p.moved && Math.hypot(dx, dy) < 8) return;
      if (!p.moved) { p.moved = true; clearTimeout(this.holdTimer); this.peek = null; }
      const cell = this.$el.clientWidth / this.span || 1;
      this.moveTo(p.from.r - dy / cell, p.from.c - dx / cell);
    },
    // The wheel glides the window as far as it scrolled, then settles once
    // it has been still for WHEEL_REST_MS (long enough for a trackpad's
    // coasting to finish). Ctrl+wheel is the browser's pinch-zoom and is
    // left alone.
    wheel(e) {
      if (!this.windowed || e.ctrlKey) return;
      e.preventDefault();
      const cell = this.$el.clientWidth / this.span || 1;
      const unit = e.deltaMode === 1 ? cell : e.deltaMode === 2 ? cell * this.span : 1;
      let dx = e.deltaX * unit;
      let dy = e.deltaY * unit;
      // Shift+wheel is sideways; most browsers already swap it, Chrome on
      // a Mac does, Windows mice often don't.
      if (e.shiftKey && !dx) { dx = dy; dy = 0; }
      this.stopGlide();
      this.moveTo(this.pos.r + dy / cell, this.pos.c + dx / cell);
      this.settleSoon(WHEEL_REST_MS);
    },
    up() {
      const p = this.press;
      this.press = null;
      if (p && !p.moved) this.tap(p.r, p.c, p.ch);
      if (p?.moved) this.settle();
      this.peekOff('touch');
    },
    cancel() {
      if (this.press?.moved) this.settle();
      this.press = null;
      this.peekOff('touch');
    },
    hover(e, r, c) {
      if (e.pointerType === 'mouse') this.peek = { row: r, col: c, by: 'mouse' };
    },
    peekOff(by) {
      if (by === 'touch') clearTimeout(this.holdTimer);
      if (this.peek?.by === by) this.peek = null;
    },
    tap(r, c, ch) {
      if (ch === '#') return;
      this.$store.dispatch('selectCell', { row: r, col: c });
      this.$emit('tap');
    },
  },
  beforeUnmount() {
    clearTimeout(this.holdTimer);
    this.stopGlide();
  },
};
</script>

<style lang="scss">
.grid {
  background: #111;
  border: 2px solid #111;
  border-radius: 4px;
  width: 100%;
  aspect-ratio: 1;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  -webkit-touch-callout: none;
  position: relative;
  // A Giant's window drags; the page must not scroll instead.
  &.grid--window { touch-action: none; overflow: hidden; }
}

.grid__cells {
  display: grid;
  grid-template-columns: repeat(var(--n), 1fr);
  grid-template-rows: repeat(var(--n), 1fr);
  gap: 1px;
  width: 100%;
  aspect-ratio: 1;
  position: relative;

  // A window's extra row and column: squares the same size as `--span`
  // across would be, the spare one hanging off the edge, and the whole lot
  // slid by the fraction of a square (--ox, --oy) the window is part-way.
  .grid--window & {
    width: calc((100% + 1px) * var(--n) / var(--span) - 1px);
    transform: translate(
      calc(var(--ox, 0) * -1 * (100% + 1px) / var(--n)),
      calc(var(--oy, 0) * -1 * (100% + 1px) / var(--n))
    );
  }
}

.grid__peek {
  position: absolute;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 2px;
  pointer-events: none;

  span {
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    color: #111;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  }
}

.cell {
  position: relative;
  background: var(--paper);
  color: var(--paper-ink);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  container-type: size;
  cursor: pointer;

  &.cell--black { background: #111; cursor: default; }
  &.cell--word { background: var(--paper-word); }
  &.cell--cursor { background: var(--paper-cursor); }
  &.cell--revealed .cell__letter { color: var(--paper-revealed); }
  &.cell--other .cell__letter { color: var(--ink-other, var(--paper-ink)); }
  &.cell--solved { background: var(--paper-solved); }
  &.cell--solved.cell--word { background: var(--paper-solved); }
  &.cell--theme { background: var(--accent); color: #211a05; animation: theme-pop 0.5s ease-out; }
  // A lit theme square is the gold of the first solver's colour, so its
  // letter is always dark ink, whoever typed it (gold on gold reads as empty).
  &.cell--theme .cell__letter { color: #211a05; }

  // Another solver's cursor: an inset ring in their colour.
  &[style*='--other-color']::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid var(--other-color);
    pointer-events: none;
    box-sizing: border-box;
  }
}

.cell__num {
  position: absolute;
  top: 0;
  left: 1px;
  font-size: clamp(6px, 28cqh, 11px);
  line-height: 1.1;
  font-weight: 600;
  opacity: 0.85;
}

.cell__letter {
  font-size: clamp(9px, 62cqh, 26px);
  font-weight: 600;
  line-height: 1;
  padding-bottom: 6%;
  font-family: 'Helvetica Neue', Arial, system-ui, sans-serif;
}

.cell__wrong {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top right, transparent 47%, var(--danger) 47%, var(--danger) 53%, transparent 53%);
  pointer-events: none;
}

@keyframes theme-pop {
  from { transform: scale(0.85); }
  to { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .cell.cell--theme { animation: none; }
}
</style>
